/**
 * PointsSection - 포인트 사용 섹션
 *
 * 로그인한 사용자만 활성화. 비로그인 시 disabled 상태로 노출.
 * 보유 포인트 / 사용 가능 포인트 / 입력란 / 전액사용 버튼 제공.
 */
import { useState, useEffect } from 'react';
import { Gift, Lock } from 'lucide-react';
import { minMoney, normalizeMoney, parseMoneyInput } from '@/utils/moneyMinor';

interface PointsSectionProps {
  totalAvailable: number;
  usableAmount: number;
  minUsePoints: number;
  maxPoints?: number; // 주문 금액 기준 최대 사용 가능 포인트
  onPointsChange: (amount: number) => void;
  pointsToUse: number;
  formatCurrency: (n: number) => string;
  disabled?: boolean;
  loginPromptLabel?: string;
  labels: {
    sectionTitle: string;
    available: string;
    usable: string;
    placeholder: string;
    apply: string;
    useAll: string;
    discountApplied: string;
    minAmount: string;
  };
}

export function PointsSection({
  totalAvailable,
  usableAmount,
  minUsePoints,
  maxPoints,
  onPointsChange,
  pointsToUse,
  formatCurrency,
  disabled = false,
  loginPromptLabel,
  labels,
}: PointsSectionProps) {
  const [inputValue, setInputValue] = useState('');

  // 부모 pointsToUse가 0으로 리셋되면 입력란도 초기화
  useEffect(() => {
    if (pointsToUse === 0) setInputValue('');
  }, [pointsToUse]);

  const handleApply = () => {
    if (disabled) return;
    const parsed = parseMoneyInput(inputValue);
    if (parsed === null) {
      return;
    }
    const val = Math.max(0, parsed);
    // maxPoints(주문 금액) 있으면 그것도 고려
    const maxAvailable = maxPoints !== undefined ? minMoney(usableAmount, maxPoints) : normalizeMoney(usableAmount);
    const clamped = minMoney(val, maxAvailable);
    // 최소 사용 포인트 미달이면 0으로 리셋
    onPointsChange(clamped >= minUsePoints ? clamped : 0);
  };

  const handleUseAll = () => {
    if (disabled) return;
    const maxAvailable = maxPoints !== undefined ? minMoney(usableAmount, maxPoints) : normalizeMoney(usableAmount);
    const clamped = Math.max(0, maxAvailable);
    // minUsePoints 미달이면 0으로 처리 (handleApply와 동일 정책)
    const finalValue = clamped >= minUsePoints ? clamped : 0;
    setInputValue(finalValue > 0 ? String(finalValue) : '');
    onPointsChange(finalValue);
  };

  const displayAvailable = disabled ? '—' : formatCurrency(totalAvailable);
  const displayUsable = disabled ? '—' : formatCurrency(usableAmount);

  return (
    <div>
      <div className="flex items-center gap-[8px] mb-[12px]">
        <Gift className="w-[18px] h-[18px] text-primary" />
        <span className="text-[15px] font-semibold text-foreground">
          {labels.sectionTitle}
        </span>
      </div>

      <div className="flex justify-between text-[13px] mb-[10px]">
        <span className="text-muted-foreground">
          {labels.available}: <span className="font-semibold text-foreground">{displayAvailable}</span>
        </span>
        <span className="text-muted-foreground">
          {labels.usable}: <span className="font-semibold text-primary">{displayUsable}</span>
        </span>
      </div>

      <div className="flex gap-[8px]">
        <input
          type="number"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={labels.placeholder}
          min={0}
          step="0.01"
          max={usableAmount}
          disabled={disabled}
          className={`flex-1 px-[12px] py-[10px] border border-border rounded-[8px] text-[14px] focus:outline-none focus:ring-2 focus:ring-primary ${disabled ? 'opacity-50 cursor-not-allowed bg-muted' : ''}`}
        />
        <button
          onClick={handleApply}
          disabled={disabled}
          className={`px-[16px] py-[10px] bg-muted border border-border rounded-[8px] text-[14px] font-semibold hover:bg-muted/80 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {labels.apply}
        </button>
        <button
          onClick={handleUseAll}
          disabled={disabled}
          className={`px-[16px] py-[10px] bg-primary text-white rounded-[8px] text-[14px] font-semibold hover:bg-primary-hover transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {labels.useAll}
        </button>
      </div>

      {pointsToUse > 0 && (
        <p className="mt-[8px] text-[12px] text-primary font-medium text-right">
          -{formatCurrency(pointsToUse)} {labels.discountApplied}
        </p>
      )}

      {!disabled && usableAmount < minUsePoints && (
        <p className="mt-[6px] text-[12px] text-muted-foreground">
          {labels.minAmount} {formatCurrency(minUsePoints)}
        </p>
      )}

      {loginPromptLabel && (
        <p className="mt-[8px] text-[12px] text-muted-foreground flex items-center gap-[4px]">
          <Lock className="w-[12px] h-[12px]" /> {loginPromptLabel}
        </p>
      )}
    </div>
  );
}
